"use client";

import React from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ArrowRight, Terminal } from "lucide-react";
const methodologySteps = [{
  id: "01",
  title: "HIPAA Compliance",
  description: "Our end-to-end encryption and strict data handling protocols ensure your medical information remains confidential and secure at every touchpoint.",
  specs: ["AES-256 Data Encryption", "Secure Patient Portals", "Audit-Ready Documentation"]
}, {
  id: "02",
  title: "Climate Control",
  description: "Advanced thermal packaging and real-time monitoring maintain the precise temperature required for sensitive medications during transit.",
  specs: ["Medical-Grade Insulation", "Thermal Sensor Logging", "Cold-Chain Verification"]
}, {
  id: "03",
  title: "Real-Time Tracking",
  description: "Full visibility from the pharmacy to your doorstep. Our logistics engine provides minute-by-minute updates and verified delivery confirmation.",
  specs: ["GPS Fleet Telemetry", "Biometric Verification", "Instant Status Alerts"]
}];
export default function TheMethodology() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="236697" id="security-protocols" className="dark bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-[0.03]"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-background via-transparent to-background"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mb-20 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary text-sm font-medium mb-6 border border-primary/20">
            <Terminal className="w-4 h-4" />
            <span className="font-mono uppercase tracking-wider">Security Protocols</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold font-default tracking-tight mb-6">
            Clinical Safety Standards
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-2xl">
            GOTMEDS.IO operates with surgical precision, ensuring every prescription is handled with the highest level of medical integrity and logistical security.
          </p>
        </div>

        {}
        <div className="flex flex-col gap-16 md:gap-24 lg:gap-32">
          {methodologySteps.map((step, i) => {
          const {
            ref: stepRef,
            isVisible: isStepVisible
          } = useRevealOnScroll({
            threshold: 0.2
          });
          return <div key={step.id} data-index={i} ref={stepRef} className={`grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-8 lg:gap-24 items-start transition-all duration-1000 delay-[100ms] ${isStepVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                {}
                <div className="font-mono text-8xl md:text-[12rem] lg:text-[16rem] font-bold text-primary/5 leading-none tracking-tighter select-none mt-[-0.1em] lg:mt-[-0.15em]">
                  {step.id}
                </div>

                {}
                <div className="pt-2 lg:pt-8 max-w-3xl">
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-semibold font-default mb-6 text-foreground">
                    {step.title}
                  </h3>
                  <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                    {step.description}
                  </p>
                  
                  {}
                  <div className="bg-card/20 border border-border/40 rounded-xl p-6 backdrop-blur-md shadow-lg">
                    <h4 className="text-sm font-mono text-secondary mb-4 uppercase tracking-widest">Technical Parameters</h4>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {step.specs.map((spec, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-sm text-muted-foreground">
                          <div className="w-1.5 h-1.5 rounded-lg bg-secondary/60 mt-1.5 shrink-0"></div>
                          <span className="font-mono">{spec}</span>
                        </li>)}
                    </ul>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {}
        <div className="mt-24 lg:mt-32 pt-12 border-t border-border/20 flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-muted-foreground font-mono text-sm">
            ATLANTA MEDICAL LOGISTICS DIVISION 
          </p>
          <Button asChild size="lg" className="rounded-lg font-mono uppercase tracking-wider bg-primary hover:bg-primary/90 shadow-md">
            <Link to="/PatientSupportPage">
              View Safety Reports <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}